/**
 * Supplier Evaluation — full production schema.
 * Mirrors reactjs/src/types/supplier-evaluation.ts exactly.
 * Database: sensei-workflows / collection: supplier-evaluation/{id}
 */

export interface SupplierCollaborator {
  name: string;
  email: string;
  token: string;
  invited_at: string;
  last_active?: string;
}

export interface EvaluatorUser {
  uid: string;
  email: string;
  displayName?: string;
  added_at: string;
}

export type SEWorkflowState =
  | "created"
  | "questionnaire_ready"
  | "sent"
  | "responded"
  | "scored"
  | "analyzed";

export interface SupplierEvaluationProject {
  evaluation_id: string;
  project_name: string;
  supplier_info: {
    company_name: string;
    contact_person: string;
    email: string;
    industry: string;
  };
  evaluation_focus: string[];
  additional_context?: string;
  workflow_state: SEWorkflowState;
  created_at: string;
  updated_at: string;
  created_by_email: string;
  created_by_uid: string;
  invitation_sent_at?: string;
  supplier_token?: string;
  supplier_completed_at?: string;
  auditor_scored_at?: string;
  analysis_generated_at?: string;
  shared_with?: SupplierCollaborator[];
  evaluator_users?: EvaluatorUser[];
  evaluator_uids?: string[];
}

export interface AssessmentQuestion {
  question_id: string;
  question_text: string;
  guidance: string;
  require_evidence?: boolean;
  supplier_score?: number | null;
  supplier_observation?: string;
  company_score?: number | null;
  company_observation?: string;
}

export interface AssessmentSection {
  section_id: string;
  section_title: string;
  weight_percent: number;
  questions: AssessmentQuestion[];
  section_score_supplier?: number;
  section_score_company?: number;
}

export interface AssessmentForm {
  form_meta: {
    schema_version: string;
    generated_at: string;
    industry_vertical: string;
    commodity_category?: string;
    design_responsible: boolean;
  };
  sections: AssessmentSection[];
  scoring_summary: {
    overall_supplier_score?: number;
    overall_company_score?: number;
  };
}

export const EVALUATION_CATEGORIES = [
  "Customer Satisfaction",
  "Quality System",
  "Business System",
  "HSE and Risk Management",
  "New Product Development",
  "Purchasing and Sub-Supplier Management",
  "Production Control and Planning",
  "Change Management",
  "Inventory Control and Housekeeping",
  "Corrective Action and Control of Non-Conforming",
  "Continuous Improvement",
  "Measurement and Test",
  "Process Walk",
] as const;

export const INDUSTRIES = [
  { value: "manufacturing", label: "Manufacturing" },
  { value: "automotive",    label: "Automotive" },
  { value: "aerospace",     label: "Aerospace" },
  { value: "pharmaceutical",label: "Pharmaceutical" },
  { value: "food_beverage", label: "Food & Beverage" },
  { value: "electronics",   label: "Electronics" },
  { value: "chemicals",     label: "Chemicals" },
  { value: "textile",       label: "Textile" },
  { value: "other",         label: "Other" },
] as const;

// State-machine helpers (mirrors supplier-evaluation-state-machine.ts)
export type EvaluationStep = 1 | 2 | 3 | 4 | 5;

export const STATE_TO_STEP: Record<SEWorkflowState, EvaluationStep> = {
  created:             1,
  questionnaire_ready: 2,
  sent:                3,
  responded:           3,
  scored:              4,
  analyzed:            5,
};

export const STEP_LABELS: Record<EvaluationStep, string> = {
  1: "Evaluation Setup",
  2: "AI Question Design",
  3: "Supplier Response",
  4: "My Ratings",
  5: "Analysis & Report",
};

export const STEP_DESCRIPTIONS: Record<EvaluationStep, string> = {
  1: "Define supplier and evaluation scope",
  2: "Generate and review the questionnaire",
  3: "Supplier completes the assessment",
  4: "Rate and score the responses",
  5: "AI-generated insights and action plan",
};

export function getStepFromState(state: SEWorkflowState): EvaluationStep {
  return STATE_TO_STEP[state];
}

export function getStateLabelShort(state: SEWorkflowState): string {
  const labels: Record<SEWorkflowState, string> = {
    created:             "Draft",
    questionnaire_ready: "Questionnaire Ready",
    sent:                "Awaiting Response",
    responded:           "Response Received",
    scored:              "Scored",
    analyzed:            "Analysis Complete",
  };
  return labels[state];
}
